import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ArrowRightCircle } from "react-feather";

/**
 * @param items - Array of items that contains {label: string, callback: function}
 * @param ref
 * @type {React.ForwardRefExoticComponent<React.PropsWithoutRef<{}> & React.RefAttributes<unknown>>}
 */
export const ActionMenu = forwardRef(
  ({ items = [], theme = "dark", className = "" }, ref) => {
    const [show, setShow] = useState(false);
    const [isReady, setIsReady] = useState(false);

    const [position, setPosition] = useState({
      top: 0,
      left: 0,
      right: "auto",
    });

    const popUpRef = useRef(null);

    useEffect(() => {
      document.addEventListener("mousedown", handleClickOutside);

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    useEffect(() => {
      const handleKeyDown = (event) => {
        if (show && event.key === "Escape") {
          setShow(false);
        }
      };

      document.addEventListener("keydown", handleKeyDown);

      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }, [show]);

    useEffect(() => {
      if (show && popUpRef.current) {
        const rect = popUpRef.current.getBoundingClientRect();
        let newPosition = { ...position };
        if (rect.right + rect.width > window.innerWidth) {
          newPosition.right = 0;
          newPosition.left = "auto";
        } else {
          newPosition.left = 0;
          newPosition.right = "auto";
        }
        if (rect.bottom + rect.height > window.innerHeight) {
          newPosition.top = "auto";
          newPosition.bottom = 0;
        } else {
          newPosition.top = 0;
          newPosition.bottom = "auto";
        }
        setPosition(newPosition);
        setIsReady(true);
      }
    }, [show]);

    useImperativeHandle(ref, () => ({
      toggleMenu() {
        setIsReady(false);
        setShow((prevShow) => !prevShow);
      },
    }));

    const handleClickOutside = (event) => {
      if (popUpRef.current && !popUpRef.current.contains(event.target)) {
        setShow(false);
      }
    };

    const handleClick = (callback) => (event) => {
      event.preventDefault();
      callback();
    };

    if (!show) return null;

    let themeColorsBody = "";
    let themeColorsText = "";

    switch (theme) {
      case "light":
        themeColorsBody = "bg-white";
        themeColorsText = "text-black hover:bg-primary-light";
        break;
      default:
        themeColorsBody = "bg-[#2f2f2f]";
        themeColorsText = "text-white hover:bg-black";
        break;
    }

    return (
      <div
        ref={popUpRef}
        style={position}
        className={`absolute flex w-[15.3125rem] flex-col items-start gap-2 rounded bg-[#2f2f2f] p-4 ${
          !isReady ? "invisible" : ""
        } ${className} ${themeColorsBody}`}
        onClick={(e) => e.preventDefault()}
      >
        {items.map((item, i) => (
          <div
            key={i}
            className={`group flex cursor-pointer items-center justify-between gap-2 self-stretch px-2 py-1 ${themeColorsText}`}
            onClick={handleClick(item.callback)}
          >
            {item.label}
            <span className="invisible group-hover:visible">
              {item.icon && React.isValidElement(item.icon) ? (
                item.icon
              ) : (
                <ArrowRightCircle />
              )}
            </span>
          </div>
        ))}
      </div>
    );
  },
);
